import React, { useState, useCallback, useEffect } from 'react';
import { Layout, Row, Col, message, Modal, Input, Checkbox, Card, Space } from 'antd';
import DragDropContext from '../components/common/DragDropContext';
import FieldPalette from '../components/ReportBuilder/FieldPalette';
import Canvas from '../components/ReportBuilder/Canvas';
import PreviewPanel from '../components/ReportBuilder/PreviewPanel';
import FilterBuilder from '../components/ReportBuilder/FilterBuilder';
import AggregationBuilder from '../components/ReportBuilder/AggregationBuilder';
import ReportServices from '../../../services/Report';

const { Content } = Layout;

const ReportBuilder = () => {
  const [selectedEntity, setSelectedEntity] = useState('items');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState([]);
  const [aggregates, setAggregates] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [loadedReportId, setLoadedReportId] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const loadEntityFields = useCallback(async (entity) => {
    try {
      const metadata = await ReportServices.getEntityMetadata(entity);
      const fields = metadata.fields.map(field => ({
        name: field.name,
        label: field.label,
        type: field.type,
        operators: field.operators,
        relationTarget: field.relationTarget
      }));
      setAvailableFields(fields);
    } catch (error) {
      console.error('Error loading entity fields:', error);
      setAvailableFields([]);
    }
  }, []);

  useEffect(() => {
    // Load initial entity fields
    loadEntityFields(selectedEntity);
  }, [selectedEntity, loadEntityFields]);

  useEffect(() => {
    // Load URL parameters to check if we need to load a saved report
    const urlParams = new URLSearchParams(window.location.search);
    const loadReportId = urlParams.get('load');
    const entityType = urlParams.get('entity');
    if (loadReportId && entityType) {
      loadSavedReport(loadReportId, entityType);
    }
  }, []);

  const loadSavedReport = async (reportId, entityType) => {
    try {
      setLoading(true);
      // Set the entity type from URL parameter
      setSelectedEntity(entityType);
      // Get the report with the correct entity type from URL
      const report = await ReportServices.getSavedSearch(entityType, reportId);
      // Process the report data
      processReportData(report);

      // Auto-execute the loaded report to show data immediately
      // Use executeSavedSearch instead of executeSearch to avoid double API calls
      // const result = await ReportService.executeSavedSearch(entityType, reportId);
      // if (result && result.data) {
      //   setReportData(result.data);
      // }
    } catch (error) {
      console.error('Error loading report:', error);
      message.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const processReportData = (report) => {
    if (report && report.criteria) {
      // Load the report metadata (title, public status, etc.)
      setReportTitle(report.name || '');
      setIsPublic(report.isPublic || false);

      // Load the report criteria into the builder
      const criteria = report.criteria;

      // Load selected fields
      if (criteria.select) {
        // Convert select fields back to field objects with entity info
        const fields = criteria.select.map(selectField => ({
          name: selectField.path, // Keep the full path as stored
          label: selectField.alias || selectField.path.split('.').pop().replace(/([A-Z])/g, ' $1').trim(),
          type: 'string', // We'll need to enhance this to get actual field types
          entityType: report.entityType || 'items' // Use the report's entity type
        }));
        setSelectedFields(fields);
      }

      // Load filters
      if (criteria.conditions) {
        const loadedFilters = criteria.conditions.map(condition => ({
          field: {
            name: condition.field, // Keep the full field path as stored
            label: condition.field.split('.').pop().replace(/([A-Z])/g, ' $1').trim(),
            type: 'string' // Can be enhanced
          },
          operator: condition.operator,
          value: condition.value,
          valueTo: condition.valueTo,
          type: 'string'
        }));
        setFilters(loadedFilters);
      }

      // Load aggregates
      if (criteria.aggregates) {
        // Filter out aggregates that have FormulaExpression objects as they can't be rendered directly in the UI
        const validAggregates = criteria.aggregates.filter(agg =>
          typeof agg.field === 'string' || !agg.field // Allow string fields and null/undefined
        );
        setAggregates(validAggregates);
      }

      // Load group by
      if (criteria.groupBy) {
        setGroupBy(criteria.groupBy);
      }

      // Store the full report data for preserving joins and other complex criteria
      setReportData(report);

      setLoadedReportId(report.id || reportId);
      message.success(`Report "${report.name}" loaded successfully`);
    }
  };

  const handleEntityChange = useCallback((entity) => {
    setSelectedEntity(entity);
    loadEntityFields(entity);
    // Don't clear fields when entity changes - allow cross-entity joins
    // Only clear report data since the query will be different
    setReportData([]);
  }, [loadEntityFields]);

  const handleAddAggregate = useCallback((aggregate) => {
    setAggregates(prev => [...prev, aggregate]);
  }, []);

  const handleRemoveAggregate = useCallback((index) => {
    setAggregates(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddGroupBy = useCallback((groupByField) => {
    setGroupBy(prev => [...prev, groupByField]);
  }, []);

  const handleRemoveGroupBy = useCallback((index) => {
    setGroupBy(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFieldAdd = useCallback((field, entityType) => {
    const fieldWithEntity = { ...field, entityType };
    setSelectedFields(prev => {
      // Check if field already exists
      const exists = prev.some(f => f.name === field.name && f.entityType === entityType);
      if (exists) {
        message.warning(`${field.label} is already added`);
        return prev;
      }
      return [...prev, fieldWithEntity];
    });
  }, []);

  const handleFieldRemove = useCallback((index) => {
    setSelectedFields(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFilterAdd = useCallback((filter) => {
    setFilters(prev => [...prev, filter]);
  }, []);

  const handleFilterRemove = useCallback((index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFilterUpdate = useCallback((index, updatedFilter) => {
    setFilters(prev => prev.map((filter, i) =>
      i === index ? updatedFilter : filter
    ));
  }, []);

  const buildSearchCriteria = useCallback(() => {
    const criteria = {
      select: selectedFields.map(field => ({
        path: field.name, // Use field name without entity prefix - API will handle entity context
        alias: field.label.replace(/\s+/g, '_').toLowerCase()
      })),
      conditions: filters.map(filter => ({
        field: filter.field.name, // Use field name without entity prefix
        operator: filter.operator,
        value: filter.value,
        valueTo: filter.valueTo
      }))
    };

    // Add aggregations if present
    if (aggregates.length > 0) {
      criteria.aggregates = aggregates.map(agg => ({
        ...agg,
        field: typeof agg.field === 'string' ? agg.field : agg.field // Keep as-is, API handles entity context
      }));
    }

    // Add group by if present
    if (groupBy.length > 0) {
      criteria.groupBy = groupBy.map(gb => ({
        ...gb,
        field: gb.field // Keep as-is, API handles entity context
      }));
    }

    // Add joins for related fields - use the saved joins if loading a report
    if (loadedReportId && reportData?.criteria?.joins) {
      criteria.joins = reportData.criteria.joins;
    } else {
      // Calculate joins for new reports
      const joins = new Set();

      // Add joins based on selected fields
      selectedFields.forEach(field => {
        if (field.name.includes('.')) {
          const parts = field.name.split('.');
          if (parts.length >= 2) {
            joins.add(parts[0]); // Add the first relation
            if (parts.length > 2) {
              // Handle nested relations like customer.address
              joins.add(`${parts[0]}.${parts[1]}`);
            }
          }
        }
      });

      // Add joins based on filters
      filters.forEach(filter => {
        if (filter.field.name.includes('.')) {
          const parts = filter.field.name.split('.');
          if (parts.length >= 2) {
            joins.add(parts[0]);
            if (parts.length > 2) {
              joins.add(`${parts[0]}.${parts[1]}`);
            }
          }
        }
      });

      // Add joins based on aggregates
      aggregates.forEach(agg => {
        if (typeof agg.field === 'string' && agg.field.includes('.')) {
          const parts = agg.field.split('.');
          if (parts.length >= 2) {
            joins.add(parts[0]);
            if (parts.length > 2) {
              joins.add(`${parts[0]}.${parts[1]}`);
            }
          }
        }
      });

      // Add joins based on group by
      groupBy.forEach(gb => {
        if (gb.field.includes('.')) {
          const parts = gb.field.split('.');
          if (parts.length >= 2) {
            joins.add(parts[0]);
            if (parts.length > 2) {
              joins.add(`${parts[0]}.${parts[1]}`);
            }
          }
        }
      });

      if (joins.size > 0) {
        criteria.joins = Array.from(joins);
      }
    }

    return {criteria:criteria};
  }, [selectedFields, filters, aggregates, groupBy]);

  const handleExecuteReport = useCallback(async () => {
    if (selectedFields.length === 0) {
      message.warning('Please add at least one field to generate a report');
      return;
    }

    setLoading(true);
    try {
      const criteria = buildSearchCriteria();
      console.log('Executing report with criteria:', criteria);
      console.log('Selected entity:', selectedEntity);

      // Use the selected entity from the dropdown for the API call
      const result = await ReportServices.executeSearch(selectedEntity, criteria);
      console.log('API result:', result);

      if (result && result.data) {
        setReportData(result.data);
        message.success(`Report generated with ${result.data.length} records`);
        // Auto-open preview modal after successful execution
        setPreviewModalVisible(true);
      } else {
        setReportData([]);
        message.info('No data found for the selected criteria');
      }
    } catch (error) {
      console.error('Error executing report:', error);
      message.error('Failed to generate report. Please try again.');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFields, selectedEntity, buildSearchCriteria]);

  const handleSaveReportOld = useCallback(async () => {
    if (selectedFields.length === 0) {
      message.warning('Please add at least one field to save a report');
      return;
    }

    // In a real implementation, show a modal to get report name and settings
    const reportName = `Report ${new Date().toLocaleString()}`;

    try {
      const criteria = buildSearchCriteria();
      await ReportService.saveSearch(selectedEntity, reportName, criteria, false, true);
      message.success('Report saved successfully');
    } catch (error) {
      console.error('Error saving report:', error);
      message.error('Failed to save report. Please try again.');
    }
  }, [selectedFields, selectedEntity, buildSearchCriteria]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handlePreview = useCallback(() => {
    setPreviewModalVisible(true);
  }, []);

  const handleSaveReport = useCallback(async () => {
    if (selectedFields.length === 0) {
      message.warning('Please add at least one field to save a report');
      return;
    }

    if (!reportTitle.trim()) {
      message.warning('Please enter a report title');
      return;
    }

    try {
      const criteria = buildSearchCriteria();
      await ReportServices.saveSearch(selectedEntity, reportTitle.trim(), criteria, isPublic, true);
      message.success('Report saved successfully');
    } catch (error) {
      console.error('Error saving report:', error);
      message.error('Failed to save report. Please try again.');
    }
  }, [selectedFields, selectedEntity, reportTitle, isPublic, buildSearchCriteria]);

  return (
    <DragDropContext>
      <div className="page-wrapper">
        <div className="content">
          <div style={{
            height: 'calc(100vh - 120px)',
            background: '#f0f2f5',
            padding: '16px',
            overflow: 'hidden'
          }}>
            <Row gutter={16} style={{ height: '100%' }}>
              {/* Left Panel - Field Palette & Builders */}
              <Col xs={24} sm={24} md={8} lg={7} xl={6} style={{ height: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', overflow: 'auto', padding: '0 4px' }}>
                  <div style={{ flex: '0 0 auto' }}>
                    <FieldPalette onEntityChange={handleEntityChange} />
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <FilterBuilder
                      onAddFilter={handleFilterAdd}
                      onAddCondition={handleFilterAdd}
                      availableFields={availableFields}
                    />
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <AggregationBuilder
                      onAddAggregate={handleAddAggregate}
                      onAddGroupBy={handleAddGroupBy}
                      availableFields={selectedFields}
                    />
                  </div>
                </div>
              </Col>

              {/* Center Panel - Report Canvas */}
              <Col xs={24} sm={24} md={16} lg={17} xl={18} style={{ height: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
                  {/* Report Configuration Header */}
                  <Card size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <span style={{ fontWeight: 'bold' }}>Report Title:</span>
                        <Input
                          placeholder="Enter report title"
                          value={reportTitle}
                          onChange={(e) => setReportTitle(e.target.value)}
                          style={{ width: '300px' }}
                        />
                        <Checkbox
                          checked={isPublic}
                          onChange={(e) => setIsPublic(e.target.checked)}
                        >
                          Make Public
                        </Checkbox>
                      </Space>
                    </Space>
                  </Card>

                  {/* Report Canvas */}
                  <Canvas
                    selectedFields={selectedFields}
                    filters={filters}
                    aggregates={aggregates}
                    groupBy={groupBy}
                    onFieldAdd={handleFieldAdd}
                    onFieldRemove={handleFieldRemove}
                    onFilterAdd={handleFilterAdd}
                    onFilterRemove={handleFilterRemove}
                    onFilterUpdate={handleFilterUpdate}
                    onAggregateRemove={handleRemoveAggregate}
                    onGroupByRemove={handleRemoveGroupBy}
                    onExecuteReport={handleExecuteReport}
                    onSaveReport={handleSaveReport}
                    onPreview={handlePreview}
                    loading={loading}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        title="Report Preview"
        visible={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ height: '80vh', overflow: 'auto' }}
        zIndex={1050}
        maskClosable={false}
      >
        <PreviewPanel
          data={reportData}
          loading={loading}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </Modal>
    </DragDropContext>
  );
};

export default ReportBuilder;